import React, { useState } from 'react';
import { Shield, ExternalLink } from 'lucide-react';
import { useTheme } from '../ThemeContext';

/**
 * AttestBadge — Displays on posts that have been attested on-chain.
 * Full mode: "Attested" pill with click-to-expand tooltip (tx hash + explorer link).
 * Compact mode: Shield icon only (for post cards in feed).
 * 
 * Props:
 *   txHash    — The Cardano transaction hash
 *   timestamp — Unix timestamp of attestation (optional)
 *   compact   — If true, icon-only. Default false.
 */
export default function AttestBadge({ txHash: rawTxHash, timestamp, compact = false }) {
  const { t } = useTheme();
  const [showTooltip, setShowTooltip] = useState(false);

  if (!rawTxHash) return null;

  // Strip any wrapping quotes from tx hash (API sometimes returns "hash" with literal quotes)
  const txHash = rawTxHash.replace(/^["']|["']$/g, '');

  // Block explorer link
  const explorerUrl = `https://cardanoscan.io/transaction/${txHash}`;
  const shortHash = txHash.slice(0, 8) + '...' + txHash.slice(-6);
  const attestDate = timestamp ? new Date(timestamp * 1000).toLocaleDateString() : null;

  if (compact) {
    return (
      <a
        href={explorerUrl}
        target="_blank"
        rel="noopener noreferrer"
        title="Attested on Cardano"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          color: t.text,
          opacity: 0.5,
          transition: 'opacity 0.2s'
        }}
        onMouseEnter={e => e.currentTarget.style.opacity = '1'}
        onMouseLeave={e => e.currentTarget.style.opacity = '0.5'}
        aria-label="View attestation on Cardano blockchain"
      >
        <Shield size={14} />
      </a>
    );
  }

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      {/* Tier 1: pill/badge — 999px radius, informational */}
      <button
        onClick={() => setShowTooltip(!showTooltip)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          background: t.tagBg,
          color: t.text,
          border: `1px solid ${t.tagBorder}`,
          borderRadius: '999px',
          padding: '4px 12px',
          fontSize: '12px',
          fontWeight: 500,
          fontFamily: 'Poppins, sans-serif',
          cursor: 'pointer',
          transition: 'border-color 0.2s'
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = t.buttonSecondaryBorderHover}
        onMouseLeave={e => {
          if (!showTooltip) e.currentTarget.style.borderColor = t.tagBorder;
        }}
        aria-label="View blockchain attestation details"
      >
        <Shield size={13} />
        Attested
      </button>

      {showTooltip && (
        <>
          {/* Click-away overlay */}
          <div
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99 }}
            onClick={() => setShowTooltip(false)}
          />
          {/* Tooltip — Tier 3: 8px radius, card-like */}
          <div style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: 0,
            background: t.surface,
            border: `1px solid ${t.border}`,
            borderRadius: '8px',
            padding: '12px 16px',
            minWidth: '280px',
            boxShadow: t.cardShadow || '0 4px 12px rgba(0,0,0,0.08)',
            zIndex: 100,
            fontFamily: 'Poppins, sans-serif'
          }}>
            <p style={{ fontSize: '11px', color: t.textSecondary, margin: '0 0 4px 0', textTransform: 'uppercase', fontWeight: 500, letterSpacing: '0.5px' }}>
              Transaction
            </p>
            <p style={{ fontSize: '13px', color: t.text, fontFamily: 'monospace', margin: '0 0 8px 0' }}>
              {shortHash}
            </p>

            {attestDate && (
              <>
                <p style={{ fontSize: '11px', color: t.textSecondary, margin: '0 0 4px 0', textTransform: 'uppercase', fontWeight: 500, letterSpacing: '0.5px' }}>
                  Attested
                </p>
                <p style={{ fontSize: '13px', color: t.text, margin: '0 0 12px 0' }}>
                  {attestDate}
                </p>
              </>
            )}

            <a
              href={explorerUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '13px',
                color: t.accent,
                fontWeight: 500,
                textDecoration: 'none'
              }}
            >
              View on Cardano <ExternalLink size={12} />
            </a>
          </div>
        </>
      )}
    </div>
  );
}